import { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert, AlertTitle, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
  Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import CommentIcon from "@mui/icons-material/Comment";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import UndoIcon from "@mui/icons-material/Undo";

import ReviewStatusChip from "../../../generic/ReviewStatusChip";
import { UploadFile } from "../../../../services/backend/UploadFileService";
import { uploadEntityDocument } from "../../../../services/backend/EntityService";
import { ENTITY_REQUEST_ERROR_LABELS } from "../../../entities/entityLabels";
import { assertPdfNotEncrypted } from "../../../../services/pdf/validatePdf";
import { normalizeNit } from "../../../entities/entityLogic";
import { getReviewDisplayStatus } from "../../../../functions";
import {
  PAYMENT_REQUEST_MAX_ANNEXES,
  PAYMENT_REQUEST_STATUS,
  REVIEW_STATUS,
  REVIEW_STATUS_UI,
} from "../../../../constants";
import {
  daysUntilRutExpiry,
  formatBytes,
  getFileRejectReason,
  isRutValid,
  isValidDate,
  maxBytesFor,
  reviewStatusOf,
  rutValidUntil,
} from "./paymentRequestLogic";
import { formatReviewMoment } from "./paymentReviewUi";

// Componente documental de la Solicitud de Pago: un slot por entrada del
// catálogo más los anexos libres. Es la única vista del documento, así que
// atiende los dos lados del ciclo:
//   - la empresa carga en borrador y, durante la corrección, reemplaza SOLO lo
//     que le devolvieron (D8: lo no devuelto queda de solo lectura);
//   - el supervisor efectivo marca revisado, devuelve con observación
//     obligatoria y deshace lo que devolvió por error.
//
// Deliberadamente plano. NO hereda el modelo de SocialSecurityModal (1440
// líneas, dos vistas ruteadas por un useState, corrección con snapshot, OCR,
// autollenado).
//
// Los documentos con source 'entity' (el RUT) no se cargan en cada solicitud:
// se muestran resueltos desde la entidad con su estado de vigencia, y solo se
// piden cuando están vencidos o cuando el supervisor los devuelve.
//
// Los colores, iconos y etiquetas de estado salen SIEMPRE de REVIEW_STATUS_UI
// (vía ReviewStatusChip): aquí no se decide ningún color de estado.

const BUCKET = process.env.REACT_APP_STORAGE_BUCKET_CONTRACTS;

const RETURNED_COLOR = REVIEW_STATUS_UI[REVIEW_STATUS.RETURNED].color;
const REVIEWED_COLOR = REVIEW_STATUS_UI[REVIEW_STATUS.REVIEWED].color;

const extensionOf = (name) => {
  const parts = String(name || "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "pdf";
};

const formatDate = (iso) => {
  if (!isValidDate(iso)) return "sin fecha";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const PaymentRequestDocuments = ({
  request,
  catalog,
  rutDocument,
  readOnly,
  onDocumentsChange,
  onAnnexesChange,
  onError,
  // El documento que vive en la entidad (el RUT) lo carga el módulo de
  // Entidades, no esta solicitud: hace falta el permiso de la EMPRESA sobre su
  // propia ficha. Sin él la acción no se ofrece, porque respondería 403.
  canUpdateEntityDocs = false,
  onEntityDocumentUpdated,
  // --- Revisión (C7) --------------------------------------------------------
  // canReview lo decide el contenedor (supervisor efectivo + solicitud
  // presentada). Cuando es false las acciones no se pintan: el motivo se
  // explica una sola vez en el aviso de la solicitud, no en cada documento.
  canReview = false,
  reviewBusyKey = null,
  onReviewDocument,
  onReturnDocument,
  onUndoReturnDocument,
  // Ventana de corrección de la empresa: cada reemplazo se persiste de
  // inmediato (el backend lo valida contra lo devuelto y responde con la
  // solicitud ya actualizada), sin pasar por el guardado de borrador.
  correcting = false,
  onCorrectDocument,
  onCorrectAnnex,
}) => {
  const user = useSelector((state) => state.user.value);
  const [busyKey, setBusyKey] = useState(null);
  const [annexDialog, setAnnexDialog] = useState(null);
  const [rutDialog, setRutDialog] = useState(null);
  const inputsRef = useRef({});

  const documents = request.documents || {};
  const annexes = request.annexes || [];

  // El estado de revisión solo tiene sentido una vez presentada: un borrador no
  // es revisable y pintarle banderas sería ruido.
  const showReview = request.status !== PAYMENT_REQUEST_STATUS.DRAFT;

  const requestDocs = useMemo(
    () => (catalog || []).filter((d) => d.source !== "entity"),
    [catalog]
  );
  const entityDocs = useMemo(
    () => (catalog || []).filter((d) => d.source === "entity"),
    [catalog]
  );

  // Sube un archivo a la ruta definitiva de la solicitud. No se usa el patrón
  // temp/ + moveFile del contrato: allí el PDF se sube antes de que exista el
  // código; aquí el borrador ya tiene requestId.
  //
  // La ruta cuelga de `entities/{nit}/` y no de `paymentRequests/` a secas
  // porque handleUploadFile confina a las cuentas de empresa a esa carpeta: el
  // NIT en la ruta ES el aislamiento, sin nada que verificar por subida. Con la
  // ruta anterior toda carga de la empresa moría en 403. El NIT se normaliza
  // para que la carpeta sea la misma que usa el módulo de Entidades y la que el
  // guardia arma con el del usuario.
  const uploadTo = async (file, key) => {
    const stamp = Date.now();
    const path = `entities/${normalizeNit(request.entityNit)}/paymentRequests/${request.contractCode}/${request.requestId}/${key}_${stamp}.${extensionOf(file.name)}`;
    const response = await UploadFile(user, file, BUCKET, path);
    if (!response) return null;
    return {
      name: file.name,
      url: response.downloadUrl,
      path,
      size: file.size,
      mimeType: file.type || null,
      hash: response.fileHash || null,
      uploadedAt: new Date().toISOString(),
    };
  };

  // Validaciones previas comunes a toda carga: tamaño contra el límite de la
  // entrada del catálogo y PDF sin contraseña (un PDF cifrado rompe el merge
  // del acta en C8).
  const validate = async (file, docDef) => {
    const reject = getFileRejectReason(docDef, file.size);
    if (reject) return reject;
    // assertPdfNotEncrypted ya ignora lo que no sea PDF.
    const encrypted = await assertPdfNotEncrypted(file);
    if (encrypted) return encrypted;
    return "";
  };

  const handlePick = async (evt, docDef) => {
    const file = evt.target.files && evt.target.files[0];
    evt.target.value = ""; // permite volver a elegir el mismo archivo
    if (!file) return;

    const problem = await validate(file, docDef);
    if (problem) {
      onError(problem);
      return;
    }

    setBusyKey(docDef.key);
    try {
      const uploaded = await uploadTo(file, docDef.key);
      if (!uploaded) {
        onError("No fue posible cargar el archivo. Intenta de nuevo.");
        return;
      }
      // En corrección el reemplazo se persiste solo (y devuelve el documento a
      // pendiente de revisión); en borrador se acumula y se guarda con el resto.
      if (correcting) {
        await onCorrectDocument(docDef.key, uploaded);
        return;
      }
      onDocumentsChange({ ...documents, [docDef.key]: uploaded });
    } finally {
      setBusyKey(null);
    }
  };

  const handleRemoveDocument = (docDef) => {
    const next = { ...documents };
    delete next[docDef.key];
    onDocumentsChange(next);
  };

  // --- Anexos libres ---------------------------------------------------------

  const handleAnnexPick = async (evt) => {
    const file = evt.target.files && evt.target.files[0];
    evt.target.value = "";
    if (!file || !annexDialog) return;

    const problem = await validate(file, { key: "annex", label: "el anexo" });
    if (problem) {
      onError(problem);
      return;
    }

    setBusyKey("annex");
    try {
      const uploaded = await uploadTo(file, `annex_${annexes.length + 1}`);
      if (!uploaded) {
        onError("No fue posible cargar el anexo. Intenta de nuevo.");
        return;
      }
      onAnnexesChange([
        ...annexes,
        { ...uploaded, id: `${Date.now()}`, title: annexDialog.title.trim() || file.name },
      ]);
      setAnnexDialog(null);
    } finally {
      setBusyKey(null);
    }
  };

  const handleRemoveAnnex = (id) => onAnnexesChange(annexes.filter((a) => a.id !== id));

  // Reemplazo de un anexo DEVUELTO durante la corrección. El anexo conserva su
  // id y su título: lo que cambia es el archivo (D10 — el servidor verifica que
  // los no devueltos no se muevan).
  const handleAnnexReplace = async (evt, index) => {
    const file = evt.target.files && evt.target.files[0];
    evt.target.value = "";
    if (!file) return;

    const problem = await validate(file, { key: "annex", label: "el anexo" });
    if (problem) {
      onError(problem);
      return;
    }

    const key = `annex:${index}`;
    setBusyKey(key);
    try {
      const uploaded = await uploadTo(file, `annex_${index + 1}`);
      if (!uploaded) {
        onError("No fue posible cargar el anexo. Intenta de nuevo.");
        return;
      }
      await onCorrectAnnex(index, uploaded);
    } finally {
      setBusyKey(null);
    }
  };

  // --- RUT resuelto desde la entidad ----------------------------------------
  // El documento vive en la ENTIDAD, así que la carga va por el módulo de
  // Entidades (handleEntity, ADD_DOCUMENT): es quien conserva versions[],
  // recalcula la completitud documental y deja bitácora en la entidad. Esta
  // solicitud solo vuelve a leerlo después. `replaceFileId` es obligatorio
  // cuando ya hay archivo en el slot: el RUT admite uno solo, y sin esa clave la
  // carga se rechazaría con DOC_AT_MAX_FILES en vez de reemplazarlo.

  const handleRutPick = async (evt) => {
    const file = evt.target.files && evt.target.files[0];
    evt.target.value = "";
    if (!file || !rutDialog) return;
    if (!isValidDate(rutDialog.issuedAt)) {
      onError("Indica la fecha de expedición del RUT");
      return;
    }

    const problem = await validate(file, rutDialog.docDef);
    if (problem) {
      onError(problem);
      return;
    }

    const docKey = rutDialog.docDef.key;
    setBusyKey(docKey);
    try {
      // Queda como versión vigente de la ENTIDAD (no de esta solicitud): la
      // anterior se conserva en versions[], y cada solicitud congela al
      // enviarse la versión que la respalda.
      const saved = await uploadEntityDocument({
        nit: request.entityNit,
        type: rutDialog.docDef.entityDocKey || docKey,
        file,
        issuedAt: rutDialog.issuedAt,
        replaceFileId: rutDocument?.fileId || null,
        user,
      });
      if (!saved.ok) {
        onError(
          ENTITY_REQUEST_ERROR_LABELS[saved.error] ||
            "No fue posible actualizar el documento de tu entidad"
        );
        return;
      }
      // El documento de la entidad cambió: el contenedor decide cómo refrescar
      // la solicitud. Si el supervisor lo había devuelto, además la devuelve a
      // pendiente y vuelve a congelar la versión que la respalda — el servidor
      // relee la entidad, así que no se le manda archivo.
      await onEntityDocumentUpdated(docKey);
      setRutDialog(null);
    } finally {
      setBusyKey(null);
    }
  };

  const renderFileRow = (file, { onRemove }) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: "break-all" }}>
        {file.name}
        {file.size ? (
          <Typography component="span" variant="caption" color="text.secondary">
            {" "}
            · {formatBytes(file.size)}
          </Typography>
        ) : null}
      </Typography>
      <Tooltip title="Abrir">
        <IconButton size="small" onClick={() => window.open(file.url, "_blank")}>
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {!readOnly && onRemove && (
        <Tooltip title="Quitar">
          <IconButton size="small" onClick={onRemove}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  // --- Revisión: estado, observación e historial -----------------------------

  // Bandera del documento con el esquema homogéneo: cargado y sin revisar en
  // Amarillo, sin cargar en Gris, devuelto en Rojo. Nada de esto se almacena.
  const renderReviewChip = (entry, present) => {
    if (!showReview) return null;
    const status = getReviewDisplayStatus(reviewStatusOf(entry), present);
    return <ReviewStatusChip status={status} prefix="Revisión" />;
  };

  // Observación de la devolución vigente: es lo único que la empresa lee para
  // saber qué rehacer, así que va visible junto al documento y no en un tooltip.
  const renderReturnMessage = (entry) => {
    if (!showReview || reviewStatusOf(entry) !== REVIEW_STATUS.RETURNED) return null;
    const review = entry.review || {};
    return (
      <Alert severity={RETURNED_COLOR} sx={{ mt: 1 }}>
        <AlertTitle sx={{ mb: 0 }}>Devuelto para corrección</AlertTitle>
        <Typography variant="body2">{review.message}</Typography>
        <Typography variant="caption" color="text.secondary">
          {review.byName || "Supervisión"} · {formatReviewMoment(review.at)}
        </Typography>
      </Alert>
    );
  };

  // Historial completo de devoluciones del documento, incluidas las de ciclos
  // anteriores y las deshechas (atenuadas y tachadas, como en el flujo
  // natural): la traza no debe mentir sobre lo que pasó.
  const renderReturnHistory = (entry) => {
    const history = (entry && entry.returnHistory) || [];
    if (!showReview || !history.length) return null;
    return (
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 0.5 }}>
              Devoluciones ({history.length})
            </Typography>
            {history.map((ret, idx) => (
              <Box key={idx} sx={{ mb: 0.5, opacity: ret.undone ? 0.6 : 1 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    textDecoration: ret.undone ? "line-through" : "none",
                  }}
                >
                  {ret.byName || "Supervisión"} — {formatReviewMoment(ret.at)}
                </Typography>
                <Typography
                  sx={{ fontSize: 12, textDecoration: ret.undone ? "line-through" : "none" }}
                >
                  {ret.message}
                </Typography>
                {ret.undone && (
                  <Typography sx={{ fontSize: 12, fontStyle: "italic" }}>
                    Deshecha el {formatReviewMoment(ret.undoneAt)}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        }
      >
        <Chip
          size="small"
          variant="outlined"
          icon={<CommentIcon />}
          color={RETURNED_COLOR}
          label={`${history.length} devolución${history.length > 1 ? "es" : ""}`}
        />
      </Tooltip>
    );
  };

  // Acciones del supervisor sobre un documento. Solo se pintan si el contenedor
  // habilitó la revisión (supervisor efectivo y solicitud presentada) y hay algo
  // que revisar: marcar revisado y devolver ocupan el mismo slot que el
  // deshacer, según el estado del documento.
  const renderReviewActions = (key, entry, present) => {
    if (!canReview || !present) return null;
    const status = reviewStatusOf(entry);
    const isReturned = status === REVIEW_STATUS.RETURNED;
    const busy = reviewBusyKey === key;

    return (
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
        {busy && <CircularProgress size={16} sx={{ mr: 0.5 }} />}
        {!isReturned && status !== REVIEW_STATUS.REVIEWED && (
          <Tooltip title="Marcar como revisado">
            <IconButton size="small" disabled={busy} onClick={() => onReviewDocument(key)}>
              <FactCheckIcon fontSize="small" color={REVIEWED_COLOR} />
            </IconButton>
          </Tooltip>
        )}
        {isReturned ? (
          <Tooltip title="Deshacer devolución">
            <IconButton size="small" disabled={busy} onClick={() => onUndoReturnDocument(key)}>
              <UndoIcon fontSize="small" color={RETURNED_COLOR} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Devolver documento (con observación)">
            <IconButton size="small" disabled={busy} onClick={() => onReturnDocument(key)}>
              <AssignmentReturnIcon fontSize="small" color={RETURNED_COLOR} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    );
  };

  const renderRequestDoc = (docDef) => {
    const entry = documents[docDef.key];
    const file = entry;
    const limit = maxBytesFor(docDef);
    const isReturned = showReview && reviewStatusOf(entry) === REVIEW_STATUS.RETURNED;
    // Durante la corrección la empresa reemplaza SOLO lo devuelto: los demás
    // documentos quedan de solo lectura (D8).
    const canReplaceInCorrection = correcting && isReturned;

    return (
      <Card
        variant="outlined"
        key={docDef.key}
        sx={{ mb: 2, ...(isReturned ? { borderColor: `${RETURNED_COLOR}.main` } : {}) }}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {docDef.label}
            </Typography>
            <Chip
              size="small"
              label={docDef.required === false ? "Opcional" : "Obligatorio"}
              color={docDef.required === false ? "default" : "primary"}
              variant="outlined"
            />
            {renderReviewChip(entry, !!file?.url)}
            {renderReturnHistory(entry)}
          </Stack>

          {file
            ? renderFileRow(file, { onRemove: () => handleRemoveDocument(docDef) })
            : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Sin cargar
              </Typography>
            )}

          {renderReturnMessage(entry)}

          {(!readOnly || canReplaceInCorrection) && (
            <>
              <input
                type="file"
                hidden
                accept={(docDef.acceptedMime || []).join(",") || undefined}
                ref={(el) => { inputsRef.current[docDef.key] = el; }}
                onChange={(evt) => handlePick(evt, docDef)}
              />
              <Button
                size="small"
                sx={{ mt: 1 }}
                color={canReplaceInCorrection ? RETURNED_COLOR : "primary"}
                startIcon={busyKey === docDef.key ? <CircularProgress size={16} /> : <UploadFileIcon />}
                disabled={busyKey === docDef.key}
                onClick={() => inputsRef.current[docDef.key]?.click()}
              >
                {canReplaceInCorrection
                  ? "Reemplazar documento devuelto"
                  : file ? "Reemplazar" : "Cargar"}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Máximo {formatBytes(limit)}
              </Typography>
            </>
          )}

          {renderReviewActions(docDef.key, entry, !!file?.url)}
        </CardContent>
      </Card>
    );
  };

  const renderEntityDoc = (docDef) => {
    // El archivo se resuelve desde la entidad (rutDocument), pero su REVISIÓN
    // se guarda en documents[key] como la de cualquier otro documento del
    // catálogo: un solo lugar donde mirar el estado de revisión.
    const doc = rutDocument;
    const entry = documents[docDef.key];
    const valid = doc && isRutValid(doc.issuedAt);
    const days = doc ? daysUntilRutExpiry(doc.issuedAt) : null;
    const isReturned = showReview && reviewStatusOf(entry) === REVIEW_STATUS.RETURNED;
    const canReplaceInCorrection = correcting && isReturned;
    // Hay que reemplazarlo cuando falta o está vencido y la solicitud todavía se
    // puede diligenciar, o cuando el supervisor lo devolvió.
    const needsEntityDocUpdate = (!readOnly && (!doc || !valid)) || canReplaceInCorrection;

    return (
      <Card
        variant="outlined"
        key={docDef.key}
        sx={{ mb: 2, ...(isReturned ? { borderColor: `${RETURNED_COLOR}.main` } : {}) }}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {docDef.label}
            </Typography>
            <Chip size="small" label="De tu empresa" variant="outlined" />
            {doc && (
              <Chip
                size="small"
                color={valid ? "success" : "error"}
                label={valid ? "Vigente" : "Vencido"}
              />
            )}
            {renderReviewChip(entry, !!doc?.url)}
            {renderReturnHistory(entry)}
          </Stack>

          {!doc && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Tu empresa no tiene {docDef.label} registrado. Cárgalo para poder enviar la solicitud.
            </Alert>
          )}

          {doc && (
            <>
              {renderFileRow({ name: doc.name || docDef.label, url: doc.url }, {})}
              <Typography variant="caption" color={valid ? "text.secondary" : "error.main"}>
                Expedido el {formatDate(doc.issuedAt)} · sirve hasta el {formatDate(rutValidUntil(doc.issuedAt))}
                {valid
                  ? days !== null && ` · vence en ${days} día(s)`
                  : days !== null && ` · venció hace ${Math.abs(days)} día(s)`}
              </Typography>
              {!valid && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  El {docDef.label} debe haberse expedido dentro de los últimos 3 meses.
                  Actualízalo para poder enviar la solicitud.
                </Alert>
              )}
            </>
          )}

          {renderReturnMessage(entry)}

          {/* Actualizar el documento de la entidad: en borrador solo si falta o
              está vencido; en corrección, además, cuando el supervisor lo
              devolvió (reemplazarlo es la única forma de cerrar el ciclo). */}
          {needsEntityDocUpdate && canUpdateEntityDocs && (
            <Button
              size="small"
              sx={{ mt: 1 }}
              color={canReplaceInCorrection ? RETURNED_COLOR : "primary"}
              startIcon={busyKey === docDef.key ? <CircularProgress size={16} /> : <UploadFileIcon />}
              disabled={busyKey === docDef.key}
              onClick={() => setRutDialog({ docDef, issuedAt: "" })}
            >
              {canReplaceInCorrection
                ? `Reemplazar ${docDef.label} devuelto`
                : `Actualizar ${docDef.label}`}
            </Button>
          )}

          {/* Sin el permiso sobre la ficha de la entidad la carga respondería
              403, así que en vez de un botón que falla se dice a quién pedirla. */}
          {needsEntityDocUpdate && !canUpdateEntityDocs && (
            <Alert severity="info" sx={{ mt: 1 }}>
              El {docDef.label} se actualiza en la ficha de tu empresa y tu cuenta no tiene
              ese permiso. Pídeselo a quien administra la entidad.
            </Alert>
          )}

          {renderReviewActions(docDef.key, entry, !!doc?.url)}
        </CardContent>
      </Card>
    );
  };

  const renderAnnex = (annex, index) => {
    const key = `annex:${index}`;
    const isReturned = showReview && reviewStatusOf(annex) === REVIEW_STATUS.RETURNED;
    const canReplaceInCorrection = correcting && isReturned;

    return (
      <Card
        variant="outlined"
        key={annex.id || key}
        sx={{ mb: 1, ...(isReturned ? { borderColor: `${RETURNED_COLOR}.main` } : {}) }}
      >
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {annex.title || annex.name}
            </Typography>
            {renderReviewChip(annex, !!annex.url)}
            {renderReturnHistory(annex)}
          </Stack>

          {renderFileRow(annex, { onRemove: () => handleRemoveAnnex(annex.id) })}
          {renderReturnMessage(annex)}

          {/* En corrección no se agregan ni se quitan anexos: solo se reemplaza
              el archivo del que fue devuelto (D10). */}
          {canReplaceInCorrection && (
            <>
              <input
                type="file"
                hidden
                ref={(el) => { inputsRef.current[key] = el; }}
                onChange={(evt) => handleAnnexReplace(evt, index)}
              />
              <Button
                size="small"
                sx={{ mt: 1 }}
                color={RETURNED_COLOR}
                startIcon={busyKey === key ? <CircularProgress size={16} /> : <UploadFileIcon />}
                disabled={busyKey === key}
                onClick={() => inputsRef.current[key]?.click()}
              >
                Reemplazar anexo devuelto
              </Button>
            </>
          )}

          {renderReviewActions(key, annex, !!annex.url)}
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Documentos obligatorios
      </Typography>
      {requestDocs.map(renderRequestDoc)}
      {entityDocs.map(renderEntityDoc)}

      <Divider sx={{ my: 3 }} />

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Anexos
        </Typography>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={annexes.length >= PAYMENT_REQUEST_MAX_ANNEXES}
            onClick={() => setAnnexDialog({ title: "" })}
          >
            Agregar anexo
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Documentos adicionales según la naturaleza del contrato. No son obligatorios
        para enviar la solicitud.
      </Typography>

      {annexes.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Sin anexos.
        </Typography>
      )}
      {annexes.map(renderAnnex)}

      {/* Anexo: primero el nombre descriptivo, después el archivo. */}
      <Dialog open={!!annexDialog} onClose={() => setAnnexDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Agregar anexo</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Nombre del anexo"
            value={annexDialog?.title || ""}
            onChange={(e) => setAnnexDialog({ ...annexDialog, title: e.target.value })}
            helperText="Con qué nombre debe verlo el supervisor"
          />
          <input
            type="file"
            hidden
            ref={(el) => { inputsRef.current.annex = el; }}
            onChange={handleAnnexPick}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnnexDialog(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!annexDialog?.title?.trim() || busyKey === "annex"}
            startIcon={busyKey === "annex" ? <CircularProgress size={16} /> : <UploadFileIcon />}
            onClick={() => inputsRef.current.annex?.click()}
          >
            Elegir archivo
          </Button>
        </DialogActions>
      </Dialog>

      {/* RUT: la fecha de expedición es el dato que gobierna la vigencia, así
          que se pide antes del archivo. */}
      <Dialog open={!!rutDialog} onClose={() => setRutDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Actualizar {rutDialog?.docDef?.label}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            El documento quedará registrado en tu empresa: no tendrás que volver a cargarlo
            en las próximas facturas mientras siga vigente.
          </Alert>
          <TextField
            fullWidth
            margin="dense"
            type="date"
            label="Fecha de expedición"
            InputLabelProps={{ shrink: true }}
            value={rutDialog?.issuedAt || ""}
            onChange={(e) => setRutDialog({ ...rutDialog, issuedAt: e.target.value })}
            helperText="Debe estar dentro de los últimos 3 meses"
          />
          <input
            type="file"
            hidden
            accept={(rutDialog?.docDef?.acceptedMime || []).join(",") || undefined}
            ref={(el) => { inputsRef.current.rut = el; }}
            onChange={handleRutPick}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRutDialog(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!isValidDate(rutDialog?.issuedAt) || busyKey === rutDialog?.docDef?.key}
            startIcon={
              busyKey === rutDialog?.docDef?.key ? <CircularProgress size={16} /> : <UploadFileIcon />
            }
            onClick={() => inputsRef.current.rut?.click()}
          >
            Elegir archivo
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mb: 2 }} />
    </>
  );
};

export default PaymentRequestDocuments;
